import { getItemsByRole } from "./imageWTY.js";

// USB identifiers
const VID_ALLWINNER = 0x1f3a;
const PID_FEL = 0x1000;
const PID_FES = 0xefe8;

// FEL commands
const FEL_VERIFY_DEVICE = 0x001;
const FEL_DOWNLOAD = 0x101;
const FEL_UPLOAD = 0x102;
const FEL_RUN = 0x103;

// FES commands
const FES_TRANSMIT = 0x201;
const FES_VERIFY = 0x401;

// Transport constants
const CHUNK_SIZE = 65536;
const TIMEOUT_MS = 10000;
const MAX_RETRIES = 3;
const FES_RECONNECT_DELAY_MS = 4000;

const CBW_SIGNATURE = new Uint8Array([0x41, 0x57, 0x55, 0x53, 0x42, 0x46, 0x45, 0x58]); // "AWUSBFEX"
const CSW_SIGNATURE = new Uint8Array([0x41, 0x57, 0x55, 0x53, 0x42, 0x53, 0x00, 0x00]); // "AWUSBS\0\0"

function buildCBW(dataLength, requestType = 0x12) {
  const buf = new ArrayBuffer(32);
  const view = new DataView(buf);
  const arr = new Uint8Array(buf);
  arr.set(CBW_SIGNATURE, 0);
  view.setUint32(8, dataLength, true);
  view.setUint32(12, 0x0c000000, true);
  view.setUint16(16, requestType, true);
  return arr;
}

function buildFelRequest(cmd, addr, len) {
  const buf = new ArrayBuffer(16);
  const view = new DataView(buf);
  view.setUint32(0, cmd, true);
  view.setUint32(4, addr, true);
  view.setUint32(8, len, true);
  return new Uint8Array(buf);
}

function buildFesTransmitRequest(tag, direction, length, address, mediaType = 0) {
  const buf = new ArrayBuffer(16);
  const view = new DataView(buf);
  view.setUint16(0, 0x1600, true);
  view.setUint8(2, tag & 0xff);
  view.setUint8(3, direction);
  view.setUint32(4, length, true);
  view.setUint32(8, address, true);
  view.setUint32(12, mediaType, true);
  return new Uint8Array(buf);
}

function verifyCSW(data) {
  if (data.byteLength < 13) {
    throw new Error(`CSW too short: ${data.byteLength} bytes`);
  }
  const arr = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  for (let i = 0; i < 8; i++) {
    if (arr[i] !== CSW_SIGNATURE[i]) {
      throw new Error("Invalid CSW signature");
    }
  }
  const status = arr[12];
  if (status !== 0) {
    throw new Error(`CSW status error: 0x${status.toString(16)}`);
  }
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function detectEndpoints(iface) {
  const alt = iface.alternate;
  let epIn = null;
  let epOut = null;
  for (const ep of alt.endpoints) {
    if (ep.direction === "in" && ep.type === "bulk") epIn = ep.endpointNumber;
    if (ep.direction === "out" && ep.type === "bulk") epOut = ep.endpointNumber;
  }
  if (epIn == null || epOut == null) {
    throw new Error("Could not find bulk IN and OUT endpoints");
  }
  return { epIn, epOut };
}

async function withTimeout(promise, ms = TIMEOUT_MS) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`USB operation timed out after ${ms}ms`)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

export class FelDevice {
  constructor(callbacks = {}) {
    this.onProgress = callbacks.onProgress || (() => {});
    this.onStatus = callbacks.onStatus || (() => {});
    this.onLog = callbacks.onLog || (() => {});
    this.device = null;
    this.epIn = null;
    this.epOut = null;
    this.fesTag = 0;
  }

  // ---- Connection ----

  async connectFEL() {
    this.onStatus("Requesting FEL device...");
    this.device = await navigator.usb.requestDevice({
      filters: [
        { vendorId: VID_ALLWINNER, productId: PID_FEL },
        { vendorId: VID_ALLWINNER, productId: PID_FES },
      ],
    });
    await this._openDevice();
    this.onLog("Connected to FEL device");
  }

  async connectFES() {
    this.onStatus("Waiting for FES device...");
    await delay(FES_RECONNECT_DELAY_MS);

    // Try previously-paired devices first
    let devices = await navigator.usb.getDevices();
    let dev = devices.find(
      (d) => d.vendorId === VID_ALLWINNER && d.productId === PID_FES
    );

    if (!dev) {
      this.onLog("FES device not found automatically, prompting user...");
      dev = await navigator.usb.requestDevice({
        filters: [{ vendorId: VID_ALLWINNER, productId: PID_FES }],
      });
    }

    this.device = dev;
    await this._openDevice();
    this.onLog("Connected to FES device");
  }

  async _openDevice() {
    const dev = this.device;
    await dev.open();

    // Log all configurations, interfaces, endpoints
    const config = dev.configuration || dev.configurations[0];
    if (!config) {
      await dev.selectConfiguration(1);
    }
    const cfg = dev.configuration;
    this.onLog(`Config #${cfg.configurationValue}: ${cfg.interfaces.length} interface(s)`);

    for (const iface of cfg.interfaces) {
      const alt = iface.alternate;
      this.onLog(`  Interface ${iface.interfaceNumber}: class=0x${alt.interfaceClass.toString(16)} subclass=0x${alt.interfaceSubclass.toString(16)} protocol=0x${alt.interfaceProtocol.toString(16)}`);
      for (const ep of alt.endpoints) {
        this.onLog(`    EP${ep.endpointNumber} ${ep.direction} ${ep.type} packetSize=${ep.packetSize}`);
      }
    }

    // Try to claim each interface and find bulk endpoints
    let foundEp = false;
    for (const iface of cfg.interfaces) {
      const alt = iface.alternate;
      if (foundEp) break;
      try {
        await dev.claimInterface(iface.interfaceNumber);
        this.onLog(`Claimed interface ${iface.interfaceNumber}`);

        let epIn = null, epOut = null;
        for (const ep of alt.endpoints) {
          if (ep.direction === "in" && ep.type === "bulk") epIn = ep.endpointNumber;
          if (ep.direction === "out" && ep.type === "bulk") epOut = ep.endpointNumber;
        }
        if (epIn != null && epOut != null) {
          this.epIn = epIn;
          this.epOut = epOut;
          this.onLog(`Using interface ${iface.interfaceNumber}: IN=${epIn}, OUT=${epOut}`);
          foundEp = true;
        } else {
          this.onLog(`Interface ${iface.interfaceNumber}: no bulk endpoints, releasing`);
          await dev.releaseInterface(iface.interfaceNumber);
        }
      } catch (e) {
        this.onLog(`Interface ${iface.interfaceNumber}: claim failed: ${e.message}`);
      }
    }

    if (!foundEp) throw new Error("No usable bulk endpoints found on any interface");
  }

  // ---- Low-level transport ----

  async _transferOut(data) {
    await withTimeout(this.device.transferOut(this.epOut, data));
  }

  async _transferIn(length) {
    const result = await withTimeout(this.device.transferIn(this.epIn, length));
    return result.data;
  }

  async _retry(fn) {
    let lastErr;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        return await fn();
      } catch (err) {
        lastErr = err;
        this.onLog(`Transfer error (attempt ${attempt + 1}/${MAX_RETRIES}): ${err.message}`);
        try {
          await this.device.clearHalt("out", this.epOut);
          await this.device.clearHalt("in", this.epIn);
        } catch (_) {
          // ignore halt-clear failures
        }
        if (attempt < MAX_RETRIES - 1) await delay(200);
      }
    }
    throw lastErr;
  }

  _hexDump(data) {
    const arr = data instanceof Uint8Array ? data : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return Array.from(arr.slice(0, 32)).map(b => b.toString(16).padStart(2, '0')).join(' ');
  }

  async awUsbWrite(data) {
    const payload = data instanceof Uint8Array ? data : new Uint8Array(data);
    await this._retry(async () => {
      const cbw = buildCBW(payload.length);
      this.onLog(`TX CBW: ${this._hexDump(cbw)}`);
      await this._transferOut(cbw);
      this.onLog(`TX DATA (${payload.length}b): ${this._hexDump(payload)}`);
      await this._transferOut(payload);
      const csw = await this._transferIn(13);
      this.onLog(`RX CSW (${csw.byteLength}b): ${this._hexDump(csw)}`);
      verifyCSW(csw);
    });
  }

  async awUsbRead(length) {
    return this._retry(async () => {
      const cbw = buildCBW(length);
      this.onLog(`TX CBW (read ${length}b): ${this._hexDump(cbw)}`);
      await this._transferOut(cbw);
      const data = await this._transferIn(length);
      this.onLog(`RX DATA (${data.byteLength}b): ${this._hexDump(data)}`);
      const csw = await this._transferIn(13);
      this.onLog(`RX CSW (${csw.byteLength}b): ${this._hexDump(csw)}`);
      verifyCSW(csw);
      return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    });
  }

  // ---- FEL commands ----

  async felRequest(cmd, addr, len) {
    await this.awUsbWrite(buildFelRequest(cmd, addr, len));
  }

  async felVerifyDevice() {
    this.onStatus("Probing device protocol...");
    const pid = this.device.productId;
    this.onLog(`Device PID: 0x${pid.toString(16)}`);

    // Try 1: Read without sending anything (some devices send hello)
    try {
      this.onLog("Probe 1: Trying raw read...");
      const raw = await withTimeout(this.device.transferIn(this.epIn, 64), 2000);
      this.onLog(`Probe 1 got ${raw.data.byteLength}b: ${this._hexDump(raw.data)}`);
    } catch (e) {
      this.onLog(`Probe 1: ${e.message}`);
    }

    // Try 2: Standard FEL verify (CBW + cmd + CSW)
    try {
      this.onLog("Probe 2: FEL verify (AWUSBFEX + cmd 0x001)...");
      const cbw = buildCBW(16);
      await this._transferOut(cbw);
      await this._transferOut(buildFelRequest(FEL_VERIFY_DEVICE, 0, 0));
      const resp = await withTimeout(this.device.transferIn(this.epIn, 64), 3000);
      this.onLog(`Probe 2 got ${resp.data.byteLength}b: ${this._hexDump(resp.data)}`);
      return resp.data;
    } catch (e) {
      this.onLog(`Probe 2: ${e.message}`);
    }

    // Try 3: CBW only, no data phase
    try {
      this.onLog("Probe 3: CBW only (no data)...");
      const cbw = buildCBW(0);
      await this._transferOut(cbw);
      const resp = await withTimeout(this.device.transferIn(this.epIn, 64), 3000);
      this.onLog(`Probe 3 got ${resp.data.byteLength}b: ${this._hexDump(resp.data)}`);
    } catch (e) {
      this.onLog(`Probe 3: ${e.message}`);
    }

    // Try 4: Raw FEL request without CBW wrapper
    try {
      this.onLog("Probe 4: Raw FEL request (no CBW wrapper)...");
      await this._transferOut(buildFelRequest(FEL_VERIFY_DEVICE, 0, 0));
      const resp = await withTimeout(this.device.transferIn(this.epIn, 64), 3000);
      this.onLog(`Probe 4 got ${resp.data.byteLength}b: ${this._hexDump(resp.data)}`);
    } catch (e) {
      this.onLog(`Probe 4: ${e.message}`);
    }

    // Try 5: USB Mass Storage inquiry (USBC signature)
    try {
      this.onLog("Probe 5: USB Mass Storage CBW (USBC)...");
      const msCbw = new Uint8Array(31);
      msCbw.set([0x55, 0x53, 0x42, 0x43], 0); // "USBC"
      msCbw[8] = 0x24; // data length
      msCbw[12] = 0x80; // direction IN
      msCbw[14] = 6;    // command length
      msCbw[15] = 0x12; // INQUIRY command
      msCbw[18] = 0x24; // allocation length
      await this._transferOut(msCbw);
      const resp = await withTimeout(this.device.transferIn(this.epIn, 64), 3000);
      this.onLog(`Probe 5 got ${resp.data.byteLength}b: ${this._hexDump(resp.data)}`);
    } catch (e) {
      this.onLog(`Probe 5: ${e.message}`);
    }

    // Try 6: Vendor control transfer IN (common init pattern)
    for (const req of [0x00, 0x01, 0x02, 0x10, 0x12, 0xFF]) {
      try {
        this.onLog(`Probe 6: Control IN vendor request=0x${req.toString(16)}...`);
        const result = await withTimeout(
          this.device.controlTransferIn({ requestType: 'vendor', recipient: 'device', request: req, value: 0, index: 0 }, 64),
          2000
        );
        this.onLog(`Probe 6 (req=0x${req.toString(16)}) got ${result.data.byteLength}b: ${this._hexDump(result.data)}`);
      } catch (e) {
        this.onLog(`Probe 6 (req=0x${req.toString(16)}): ${e.message}`);
      }
    }

    // Try 7: Standard USB GET_STATUS
    try {
      this.onLog("Probe 7: GET_STATUS...");
      const result = await withTimeout(
        this.device.controlTransferIn({ requestType: 'standard', recipient: 'device', request: 0x00, value: 0, index: 0 }, 2),
        2000
      );
      this.onLog(`Probe 7 got ${result.data.byteLength}b: ${this._hexDump(result.data)}`);
    } catch (e) {
      this.onLog(`Probe 7: ${e.message}`);
    }

    // Try 8: After sending control OUT, then try bulk read
    try {
      this.onLog("Probe 8: Control OUT vendor then bulk read...");
      await withTimeout(
        this.device.controlTransferOut({ requestType: 'vendor', recipient: 'device', request: 0x12, value: 0, index: 0 }, new Uint8Array(0)),
        2000
      );
      this.onLog("Probe 8: Control OUT sent, trying bulk read...");
      const resp = await withTimeout(this.device.transferIn(this.epIn, 64), 3000);
      this.onLog(`Probe 8 got ${resp.data.byteLength}b: ${this._hexDump(resp.data)}`);
    } catch (e) {
      this.onLog(`Probe 8: ${e.message}`);
    }

    // Try 9: Read from EP1 (same as OUT) - some devices use bidirectional
    try {
      this.onLog("Probe 9: transferIn on EP1 (OUT endpoint)...");
      const resp = await withTimeout(this.device.transferIn(1, 64), 2000);
      this.onLog(`Probe 9 got ${resp.data.byteLength}b: ${this._hexDump(resp.data)}`);
    } catch (e) {
      this.onLog(`Probe 9: ${e.message}`);
    }

    this.onLog("All probes done. Check log for device responses.");
    this.onLog("RECOMMENDATION: Use Wireshark + USBPcap to capture the HPRT tool traffic for exact protocol.");
    return new Uint8Array(32);
  }

  async felWrite(addr, data) {
    const payload = data instanceof Uint8Array ? data : new Uint8Array(data);
    const total = payload.length;
    let offset = 0;

    while (offset < total) {
      const end = Math.min(offset + CHUNK_SIZE, total);
      const chunk = payload.subarray(offset, end);
      await this.felRequest(FEL_DOWNLOAD, addr + offset, chunk.length);
      await this.awUsbWrite(chunk);
      offset = end;
      this.onProgress(Math.round((offset / total) * 100));
    }
  }

  async felRead(addr, len) {
    await this.felRequest(FEL_UPLOAD, addr, len);
    return this.awUsbRead(len);
  }

  async felExecute(addr) {
    this.onLog(`Executing code at 0x${addr.toString(16)}`);
    await this.felRequest(FEL_RUN, addr, 0);
  }

  // ---- FES commands ----

  async fesTransmit(data, addr, mediaType = 0) {
    const payload = data instanceof Uint8Array ? data : new Uint8Array(data);
    const total = payload.length;
    let offset = 0;

    while (offset < total) {
      const end = Math.min(offset + CHUNK_SIZE, total);
      const chunk = payload.subarray(offset, end);
      const tag = this.fesTag++ & 0xff;
      const req = buildFesTransmitRequest(tag, 0x02, chunk.length, addr + offset, mediaType);

      await this.awUsbWrite(req);
      await this.awUsbWrite(chunk);
      offset = end;
      this.onProgress(Math.round((offset / total) * 100));
    }
  }

  async fesVerify() {
    this.onStatus("Verifying flash integrity...");
    const req = buildFelRequest(FES_VERIFY, 0, 0);
    await this.awUsbWrite(req);
    const result = await this.awUsbRead(16);
    this.onLog(`Verify result: ${Array.from(result).map((b) => b.toString(16).padStart(2, "0")).join(" ")}`);
    return result;
  }

  // ---- High-level firmware update ----

  async flashFirmware(parsedImage) {
    const { header, items } = parsedImage;
    const roles = getItemsByRole(items);

    // Step 1: Verify FEL connection (should already be connected via handleConnect)
    if (!this.device) {
      await this.connectFEL();
      await this.felVerifyDevice();
    }

    // Step 2: Upload FES bootstrap to SRAM
    if (!roles.fes) throw new Error("Firmware image missing FES_1 item");
    this.onStatus("Uploading FES bootstrap...");
    this.onProgress(0);
    await this.felWrite(0x0000, roles.fes.data);
    this.onLog("FES bootstrap uploaded");

    // Step 3: Execute FES — device will reboot into FES mode
    this.onStatus("Launching FES...");
    await this.felExecute(0x0000);

    // Step 4: Close FEL connection, wait for FES re-enumeration
    try {
      await this.device.close();
    } catch (_) {
      // device may already have disconnected
    }
    this.device = null;

    // Step 5: Reconnect in FES mode
    await this.connectFES();

    // Step 6: Write firmware items
    const writeItems = [
      { name: "MBR", item: roles.mbr },
      { name: "BOOT0", item: roles.boot0 },
      { name: "UBOOT", item: roles.uboot },
      { name: "BOOT-RESOURCE", item: roles.bootResource },
      { name: "ENV", item: roles.env },
      { name: "SCRIPT", item: roles.script },
      { name: "KERNEL", item: roles.kernel },
    ];

    // Prefer FULLIMG if available — single write covers everything
    if (roles.fullimg) {
      this.onStatus("Writing full image...");
      this.onProgress(0);
      await this.fesTransmit(roles.fullimg.data, 0);
      this.onLog("Full image written");
    } else {
      let addr = 0;
      for (const { name, item } of writeItems) {
        if (!item) {
          this.onLog(`Skipping ${name} (not present)`);
          continue;
        }
        this.onStatus(`Writing ${name}...`);
        this.onProgress(0);
        await this.fesTransmit(item.data, addr);
        this.onLog(`${name} written (${item.data.length} bytes)`);
        addr += item.data.length;
      }
    }

    // Step 7: Verify and finish
    await this.fesVerify();
    this.onStatus("Firmware update complete");
    this.onProgress(100);
    this.onLog("Done");
  }

  async close() {
    if (this.device) {
      try {
        await this.device.close();
      } catch (_) {
        // ignore
      }
      this.device = null;
    }
    this.epIn = null;
    this.epOut = null;
  }
}
